import axios, { AxiosError, type AxiosResponse, type InternalAxiosRequestConfig } from 'axios';
import type { ApiEnvelope, PageMeta } from '@parkline/shared';

/** Ключи localStorage. Один источник правды для api.ts и auth.tsx. */
export const TOKEN_KEY = 'parkline.token';
export const USER_KEY = 'parkline.user';

export const LOGIN_PATH = '/login';
const LOGIN_ENDPOINT = '/auth/login';
const HTTP_UNAUTHORIZED = 401;
const DEFAULT_ERROR = 'Сервис недоступен. Повторите попытку позже.';
const DEFAULT_LIMIT = 20;

/** Тело ответа после разворачивания конверта. */
export interface Unwrapped<T> {
  data: T;
  meta?: PageMeta;
}

export type QueryParams = Record<string, string | number | boolean | undefined | null>;

export function readToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function clearSession(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
}

export const http = axios.create({
  baseURL: '/api/v1',
  headers: { 'Content-Type': 'application/json' },
});

http.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = readToken();
  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }
  return config;
});

/**
 * Ответ бэкенда всегда обёрнут в ApiEnvelope. Разворачиваем его здесь, чтобы
 * страницы работали с чистыми данными и никогда не проверяли success руками.
 */
http.interceptors.response.use(
  (response: AxiosResponse<unknown>) => {
    const envelope = response.data as ApiEnvelope<unknown> | null;
    if (envelope && typeof envelope === 'object' && 'success' in envelope) {
      if (!envelope.success) {
        throw new Error(envelope.error ?? DEFAULT_ERROR);
      }
      return { ...response, data: { data: envelope.data, meta: envelope.meta } };
    }
    // Ответ без конверта (например, статика или прокси-ошибка) — пробрасываем как есть.
    return { ...response, data: { data: response.data } };
  },
  (error: AxiosError<ApiEnvelope<unknown>>) => {
    if (error.response?.status === HTTP_UNAUTHORIZED) {
      handleUnauthorized(error.config?.url);
    }
    return Promise.reject(new Error(extractMessage(error)));
  },
);

function extractMessage(error: AxiosError<ApiEnvelope<unknown>>): string {
  const fromEnvelope = error.response?.data?.error;
  if (fromEnvelope) {
    return fromEnvelope;
  }
  return error.message || DEFAULT_ERROR;
}

/**
 * Истёкший токен: чистим сессию и уводим на логин через window.location —
 * интерцептор живёт вне дерева React, хуки роутера здесь недоступны.
 */
function handleUnauthorized(url?: string): void {
  if (url?.includes(LOGIN_ENDPOINT)) {
    return; // неверный логин/пароль — это не истёкшая сессия
  }
  clearSession();
  if (window.location.pathname !== LOGIN_PATH) {
    window.location.assign(LOGIN_PATH);
  }
}

/** Пустые значения в query не отправляем — бэкенд с forbidNonWhitelisted строг. */
function cleanParams(params?: QueryParams): QueryParams | undefined {
  if (!params) {
    return undefined;
  }
  const entries = Object.entries(params).filter(
    ([, value]) => value !== undefined && value !== null && value !== '',
  );
  return entries.length > 0 ? Object.fromEntries(entries) : undefined;
}

export async function get<T>(url: string, params?: QueryParams): Promise<T> {
  const response = await http.get<Unwrapped<T>>(url, { params: cleanParams(params) });
  return response.data.data;
}

export async function post<T>(url: string, body?: unknown): Promise<T> {
  const response = await http.post<Unwrapped<T>>(url, body ?? {});
  return response.data.data;
}

export async function patch<T>(url: string, body?: unknown): Promise<T> {
  const response = await http.patch<Unwrapped<T>>(url, body ?? {});
  return response.data.data;
}

export async function del<T>(url: string, params?: QueryParams): Promise<T> {
  const response = await http.delete<Unwrapped<T>>(url, { params: cleanParams(params) });
  return response.data.data;
}

/**
 * Страничная выборка: бэкенд отдаёт массив в data, а total/page/limit — в meta.
 */
export async function getPaged<T>(
  url: string,
  params?: QueryParams,
): Promise<{ items: T[]; meta: PageMeta }> {
  const response = await http.get<Unwrapped<T[]>>(url, { params: cleanParams(params) });
  const items = response.data.data ?? [];
  const meta = response.data.meta ?? {
    total: items.length,
    page: 1,
    limit: items.length || DEFAULT_LIMIT,
  };
  return { items, meta };
}
